import { useEffect, useMemo } from 'react';
import { useBulkSelection } from './useBulkSelection';

export interface UseTableSelectionOptions<T extends { id: string }> {
  items: T[];
  onBulkDelete?: (ids: string[]) => void;
  onRegisterToggleAll?: (fn: () => void) => void;
  onRegisterBulkDelete?: (fn: () => void) => void;
  onRegisterClearSelection?: (fn: () => void) => void;
  onSelectedCountChange?: (count: number) => void;
  onIsAllSelectedChange?: (isAll: boolean) => void;
  onSelectedItemsChange?: (selected: T[]) => void;
}

export interface UseTableSelectionReturn {
  isAllSelected: boolean;
  selectedCount: number;
  handleSelectAll: () => void;
  handleSelectItem: (id: string) => void;
  handleBulkAction: () => void;
  isSelected: (id: string) => boolean;
}

/**
 * Selection logic for data tables: bulk selection + sync with parent (toggle all, bulk delete, selected count, selected items).
 * Use with TableSelectionHeader and TableSelectionCell for consistent behavior across LeadTable, PaymentTable, etc.
 */
export function useTableSelection<T extends { id: string }>({
  items,
  onBulkDelete,
  onRegisterToggleAll,
  onRegisterBulkDelete,
  onRegisterClearSelection,
  onSelectedCountChange,
  onIsAllSelectedChange,
  onSelectedItemsChange,
}: UseTableSelectionOptions<T>): UseTableSelectionReturn {
  const {
    isAllSelected,
    selectedCount,
    handleSelectAll,
    handleSelectItem,
    handleBulkAction,
    isSelected,
    clearSelection,
  } = useBulkSelection({ items, onBulkAction: onBulkDelete });

  useEffect(() => {
    if (onRegisterToggleAll) onRegisterToggleAll(handleSelectAll);
    if (onRegisterBulkDelete) onRegisterBulkDelete(() => handleBulkAction());
    if (onRegisterClearSelection) onRegisterClearSelection(clearSelection);
  }, [onRegisterToggleAll, onRegisterBulkDelete, onRegisterClearSelection, handleSelectAll, handleBulkAction, clearSelection]);

  useEffect(() => {
    if (onSelectedCountChange) onSelectedCountChange(selectedCount);
  }, [selectedCount, onSelectedCountChange]);

  useEffect(() => {
    if (onIsAllSelectedChange) onIsAllSelectedChange(isAllSelected);
  }, [isAllSelected, onIsAllSelectedChange]);

  const itemsKey = useMemo(() => items.map((i) => i.id).join(','), [items]);
  useEffect(() => {
    if (onSelectedItemsChange) {
      const selected = items.filter((item) => isSelected(item.id));
      onSelectedItemsChange(selected);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- items/isSelected read from closure; deps chosen to avoid loop
  }, [selectedCount, itemsKey, onSelectedItemsChange]);

  return {
    isAllSelected,
    selectedCount,
    handleSelectAll,
    handleSelectItem,
    handleBulkAction,
    isSelected,
  };
}
